// client/src/components/dashboard/account/ContactForm.js // Path assumed
import React, { useState, useEffect } from 'react';
// import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
// Import icons used
import { FaEdit, FaSave, FaCheckCircle, FaTimesCircle, FaCog  } from 'react-icons/fa';
import countriesList from '../../../utils/countries'; // Assuming path is correct
import LoadingSpinner from '../../common/LoadingSpinner';
// Use REAL functions from authApi.js
import { fetchMyContactInfo, updateMyContactInfo /*, sendSecondaryEmailVerification */ } from '../../../api/authApi';

import './ContactForm.css'; // Assumes CSS file exists at this path

// Initial empty form data structure
const initialFormData = {
    emailPrimary: '', emailSecondary: '', phonePrimary: '', phoneSecondary: '',
    addressStreet: '', addressCity: '', addressPostalCode: '', addressCountry: '',
    addressState: '',
    emailSecondaryVerified: false, // Added state for verification status
    // phoneSecondaryVerified: false, // Keep if needed for phone verification
};

function ContactForm() {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';

    // State
    const [contactData, setContactData] = useState(null);
    const [formData, setFormData] = useState(initialFormData);
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(true); // Main loading for initial fetch
    const [actionLoading, setActionLoading] = useState(false); // Specific loading for save action
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');
    // const successAchievedRef = useRef(false); // For potential StrictMode issues if needed later

    // Effect to fetch initial data
    useEffect(() => {
        let isMounted = true; // Prevent state update on unmounted component
        const loadContactInfo = async () => {
            setLoading(true); setError(null); setMessage('');
            try {
                const data = await fetchMyContactInfo(); // Use REAL API function
                if (data && isMounted) {
                    setContactData(data);
                    // Populate form state, including verification status
                    setFormData({
                        emailPrimary: data.email_primary || '',
                        emailSecondary: data.email_secondary || '',
                        phonePrimary: data.phone_primary || '',
                        phoneSecondary: data.phone_secondary || '',
                        addressStreet: data.address_street || '',
                        addressCity: data.address_city || '',
                        addressPostalCode: data.address_postal_code || '',
                        addressCountry: data.address_country || '',
                        addressState: data.address_state || '',
                        emailSecondaryVerified: data.email_secondary_verified || false, // Populate status
                        // phoneSecondaryVerified: data.phone_secondary_verified || false,
                    });
                } else if (isMounted) {
                    // Handle case where fetchMyContactInfo returns null/undefined but no error (e.g., expected 404 was handled in API layer)
                     setContactData(null); setFormData(initialFormData);
                     console.log("Contact info fetch returned no data, initializing empty form.");
                }
            } catch (err) {
                 console.error("Error loading contact info:", err);
                 if (isMounted) {
                     // Check for 404 specifically if handleApiError doesn't make it distinct
                     if (err.message.includes('404') || err.response?.status === 404) {
                          console.log("Contact info not found (404), initializing empty form.");
                          setContactData(null); setFormData(initialFormData);
                     } else { setError(err.message || t('errors.api.requestFailed')); }
                 }
            } finally {
                 if (isMounted) setLoading(false);
            }
        };
        loadContactInfo();
        return () => { isMounted = false }; // Cleanup
    }, [t]); // Re-fetch if language changes? Maybe not needed.

    // Handle input changes
    const handleInputChange = (event) => {
         const { name, value } = event.target;
         setFormData(prevData => {
             const newData = { ...prevData, [name]: value };
             // Reset verification status if secondary email is changed
             if (name === 'emailSecondary') {
                 newData.emailSecondaryVerified = false;
             }
             // Add similar logic for phoneSecondaryVerified if implementing phone verification
             // if (name === 'phoneSecondary') {
             //     newData.phoneSecondaryVerified = false;
             // }
             return newData;
         });
         // Clear messages/errors on input change
         if (error) setError(null);
         if (message) setMessage('');
     };

    // Toggle edit mode / Cancel changes
    const toggleEditMode = () => {
         const currentlyEditing = !editMode;
         setEditMode(currentlyEditing);
         setError(null); setMessage('');
         if (!currentlyEditing) { // Reset form data only when exiting edit mode (Cancel)
             if (contactData) {
                 // Reset formData including verification status from last saved data
                 setFormData({
                     emailPrimary: contactData.email_primary || '',
                     emailSecondary: contactData.email_secondary || '',
                     phonePrimary: contactData.phone_primary || '',
                     phoneSecondary: contactData.phone_secondary || '',
                     addressStreet: contactData.address_street || '',
                     addressCity: contactData.address_city || '',
                     addressPostalCode: contactData.address_postal_code || '',
                     addressCountry: contactData.address_country || '',
                     addressState: contactData.address_state || '',
                     emailSecondaryVerified: contactData.email_secondary_verified || false,
                     // phoneSecondaryVerified: contactData.phone_secondary_verified || false,
                 });
             } else {
                 // Reset to empty if no data was ever loaded
                 setFormData(initialFormData);
             }
         }
     };

    // Handle form submission using REAL function
    const handleSubmit = async (event) => {
        if (event) event.preventDefault();
        setError(null); setMessage('');
        // --- Optional Validation ---
        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        // No validation needed for disabled emailPrimary
        if (formData.emailSecondary && !isEmail.test(formData.emailSecondary)) {
            setError(t('dashboard.account.contact.errors.invalidEmailSecondary', 'Invalid Secondary Email format'));
            return;
        }
        // --- End Validation ---

        setActionLoading(true); // Start loading indicator for submit action
        try {
            // Prepare payload (send snake_case, exclude non-editable primaries)
            const payload = {
                email_secondary: formData.emailSecondary.trim() || null,
                phone_secondary: formData.phoneSecondary.trim() || null,
                address_street: formData.addressStreet.trim() || null,
                address_city: formData.addressCity.trim() || null,
                address_postal_code: formData.addressPostalCode.trim() || null,
                address_country: formData.addressCountry || null,
                address_state: formData.addressState.trim() || null,
                // Include language if backend needs it for verification email trigger
                language: i18n.language
            };
            console.log("Attempting to update contact info with payload:", payload);

            const updatedContactInfo = await updateMyContactInfo(payload); // Use REAL API function

            console.log("Contact info update successful:", updatedContactInfo);
            setMessage(t('dashboard.account.contact.updateSuccess', 'Contact info updated successfully!'));
            setContactData(updatedContactInfo); // Store latest saved data
            // Update form state from response, including latest verification status
            setFormData({
                 emailPrimary: updatedContactInfo.email_primary || '', // Keep updated
                 emailSecondary: updatedContactInfo.email_secondary || '',
                 phonePrimary: updatedContactInfo.phone_primary || '', // Keep updated
                 phoneSecondary: updatedContactInfo.phone_secondary || '',
                 addressStreet: updatedContactInfo.address_street || '',
                 addressCity: updatedContactInfo.address_city || '',
                 addressPostalCode: updatedContactInfo.address_postal_code || '',
                 addressCountry: updatedContactInfo.address_country || '',
                 addressState: updatedContactInfo.address_state || '',
                 emailSecondaryVerified: updatedContactInfo.email_secondary_verified || false, // Update status
                 // phoneSecondaryVerified: updatedContactInfo.phone_secondary_verified || false,
             });
            setEditMode(false); setError(null);
        } catch (err) {
            console.error("Error updating contact info:", err);
            setError(err.message || t('errors.api.requestFailed')); // Show translated error
        } finally {
            setActionLoading(false); // Stop loading indicator for submit action
        }
    };

    // Show loading spinner only during the initial data fetch
    if (loading) { return <LoadingSpinner />; }

    // --- Render Form ---
    return (
        <form onSubmit={handleSubmit} className="contact-form">
             {message && <div className="form-success">{message}</div>}
             {error && <div className="form-error">{error}</div>}

             {/* Email Fields */}
             <div className="form-row">
                  <div className="form-group">
                      <label htmlFor="emailPrimary">{t('dashboard.account.contact.emailPrimary')}:</label>
                      <div className="input-with-icon">
                          <input type="email" id="emailPrimary" name="emailPrimary" value={formData.emailPrimary} readOnly disabled={true} className="disabled-input-with-icon"/>
                          {formData.emailPrimary ? (<FaCheckCircle className="verified-icon" title={t('dashboard.account.contact.verified')} /> ) : (<FaTimesCircle className="not-verified-icon" title={t('dashboard.account.contact.notSet')} />)}
                      </div>
                  </div>
                  <div className="form-group">
                      <label htmlFor="emailSecondary">{t('dashboard.account.contact.emailSecondary')}:</label>
                      <div className="input-with-icon">
                          <input type="email" id="emailSecondary" name="emailSecondary" value={formData.emailSecondary} onChange={handleInputChange} disabled={!editMode} />
                          {/* Conditional Icon for Secondary Email */}
                          {formData.emailSecondary && formData.emailSecondaryVerified && (<FaCheckCircle className="verified-icon" title={t('dashboard.account.contact.verified')} /> )}
                          {formData.emailSecondary && !formData.emailSecondaryVerified && (<FaTimesCircle className="not-verified-icon" title={t('dashboard.account.contact.notVerified')} /> )}
                      </div>
                       {/* Verification Button REMOVED */}
                  </div>
              </div>

              {/* Phone Fields */}
              <div className="form-row">
                  <div className="form-group">
                     <label htmlFor="phonePrimary">{t('dashboard.account.contact.phonePrimary')}:</label>
                     <div className="input-with-icon">
                          <input type="tel" id="phonePrimary" name="phonePrimary" value={formData.phonePrimary} readOnly disabled={true} className="disabled-input-with-icon"/>
                          {formData.phonePrimary ? (<FaCheckCircle className="verified-icon" title={t('dashboard.account.contact.verified')} /> ) : (<FaCog   className="settings-icon" title={t('dashboard.account.contact.notSet')} />)}
                      </div>
                  </div>
                 <div className="form-group">
                      <label htmlFor="phoneSecondary">{t('dashboard.account.contact.phoneSecondary')}:</label>
                      <input type="tel" id="phoneSecondary" name="phoneSecondary" value={formData.phoneSecondary} onChange={handleInputChange} disabled={!editMode} />
                      {/* Add conditional icon for secondary phone if verification is implemented */}
                 </div>
              </div>

             {/* Address Fields */}
             <div className="form-row">
                  <div className="form-group form-group-span-2"> {/* Spans 2 columns */}
                      <label htmlFor="addressStreet">{t('dashboard.account.contact.addressStreet')}:</label>
                      <input type="text" id="addressStreet" name="addressStreet" value={formData.addressStreet} onChange={handleInputChange} disabled={!editMode} placeholder={t('dashboard.account.contact.addressStreetPlaceholder')} />
                  </div>
             </div>
             <div className="form-row">
                  <div className="form-group">
                      <label htmlFor="addressCity">{t('dashboard.account.contact.addressCity')}:</label>
                      <input type="text" id="addressCity" name="addressCity" value={formData.addressCity} onChange={handleInputChange} disabled={!editMode} />
                  </div>
                  <div className="form-group">
                      <label htmlFor="addressState">{t('dashboard.account.contact.addressState')}:</label>
                      <input type="text" id="addressState" name="addressState" value={formData.addressState} onChange={handleInputChange} disabled={!editMode} />
                  </div>
             </div>
             <div className="form-row">
                 <div className="form-group">
                      <label htmlFor="addressPostalCode">{t('dashboard.account.contact.addressPostalCode')}:</label>
                      <input type="text" id="addressPostalCode" name="addressPostalCode" value={formData.addressPostalCode} onChange={handleInputChange} disabled={!editMode} />
                  </div>
                  <div className="form-group">
                      <label htmlFor="addressCountry">{t('dashboard.account.contact.addressCountry')}:</label>
                      <select id="addressCountry" name="addressCountry" value={formData.addressCountry || ''} onChange={handleInputChange} disabled={!editMode}>
                          <option value="" >{t('dashboard.account.contact.selectCountry')}</option>
                          {countriesList.map(country => (<option key={country.value} value={country.value}>{t(`countries.${country.value}`, country.label)}</option>))}
                      </select>
                 </div>
              </div>

            {/* Button group */}
            <div className={`button-group ${isRTL ? 'rtl' : ''}`}>
                 <button type="button" onClick={editMode ? handleSubmit : toggleEditMode} className={editMode ? 'save-button' : 'edit-button'} disabled={loading || actionLoading}> {/* Disable if fetching OR saving */}
                     {editMode ? ( <>{actionLoading ? t('dashboard.account.contact.updating') : <><FaSave /> {t('dashboard.account.contact.update')}</>}</> ) : ( <><FaEdit /> {t('dashboard.account.contact.edit')}</> )}
                 </button>
            </div>
        </form>
    );
}

export default ContactForm;